"use client"

import { useEffect, useRef } from "react"

/**
 * One shared observer for every reveal on the page: sections opt in with a ref,
 * get `data-inview="true"` once, and CSS owns the actual motion (including the
 * per-child stagger and any ambient loops gated behind the attribute).
 */
let sharedObserver: IntersectionObserver | null = null

function getSharedObserver(): IntersectionObserver | null {
  if (typeof IntersectionObserver === "undefined") return null

  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          ;(entry.target as HTMLElement).dataset.inview = "true"
          sharedObserver?.unobserve(entry.target)
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0 },
    )
  }

  return sharedObserver
}

export function useReveal<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null)

  useEffect(() => {
    const node = ref.current
    if (!node || node.dataset.inview === "true") return

    const observer = getSharedObserver()
    if (!observer) {
      node.dataset.inview = "true"
      return
    }

    observer.observe(node)
    return () => observer.unobserve(node)
  }, [])

  return ref
}
